import React from "react";
import favicon from "@images/favicon.ico";

const defaultTitle = "Mangomint | Salon & Spa Software | Booking & Management";
const defaultDescription =
  "Mangomint is the highest rated scheduling software for hair salons, spas, nail salons, barbershops, tanning salons, massage studios, tattoo shops and more.";
const defaultImage = "/images/mangomint.jpg";
const defaultLogo = "/images/mangomint-logo.png";

const getJsonLdSchema = ({ title, description, url, image }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  url,
  name: title,
  logo: defaultLogo,
  image: image || defaultImage,
  description: description?.description || description,
  publisher: {
    "@type": "Organization",
    name: "Mangomint",
    logo: {
      "@type": "ImageObject",
      url: defaultLogo,
    },
  },
});

const SeoMetadata = ({
  title,
  description,
  ogImage,
  ogTitle,
  ogDescription,
  noIndex = false,
  noFollow = false,
  noAiScraping = false,
  url,
  additionalSchemas = {},
  skipBrandingSuffix = false,
}) => {
  const addBrandingToTitle = (title) => {
    if (!title) return null;
    if (skipBrandingSuffix) return title;
    const lowerTitle = title.toLowerCase();

    // If title already contains any form of Mangomint branding, don't add more
    const hasExistingBranding = lowerTitle.includes("mangomint |") || lowerTitle.includes("| mangomint");

    if (hasExistingBranding) {
      return title;
    }

    return `${title} | Mangomint Salon and Spa Software`;
  };

  const seo = {
    title: addBrandingToTitle(title) || defaultTitle,
    description: description || defaultDescription,
    ogImage: ogImage || defaultImage,
    ogTitle: ogTitle || addBrandingToTitle(title) || defaultTitle,
    ogDescription: ogDescription || description || defaultDescription,
    url: url || (typeof window !== "undefined" ? window.location.href : undefined),
  };

  const robots = [
    noIndex ? "noindex" : "index",
    noFollow ? "nofollow" : "follow",
    noAiScraping ? "noai, noimageai" : null,
  ]
    .filter(Boolean)
    .join(", ");

  const schema = getJsonLdSchema({
    title: seo.ogTitle,
    description: seo.description,
    url: seo.url,
    image: seo.ogImage?.url || seo.ogImage,
  });

  return (
    <>
      <html lang="en" />
      {robots && <meta name="robots" content={robots} />}
      {seo.title && <title>{seo.title}</title>}
      {seo.description && <meta name="description" content={seo.description?.description || seo.description} />}
      <meta name="author" content="Mangomint" />
      <meta property="og:site_name" content="Mangomint" />
      <meta property="og:publisher" content="https://www.mangomint.com" />
      <meta property="og:title" content={seo.ogTitle} />
      <meta
        property="og:description"
        content={seo.ogDescription?.ogDescription || seo.description?.description || seo.description}
      />
      {seo.ogImage && <meta property="og:image" content={seo.ogImage.url || seo.ogImage} />}
      <meta property="og:type" content="website" />
      {schema && (
        <script
          type="application/ld+json"
          id="contentful-schema"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}

      {Object.entries(additionalSchemas).map(([key, schemaData]) => (
        <script
          key={key}
          type="application/ld+json"
          id={`${key}-schema`}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      ))}
      <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      <link rel="icon" type="image/x-icon" href={favicon} />
    </>
  );
};

export { SeoMetadata };
